// @flow
// eslint-disable-next-line react/no-deprecated
import { hydrateRoot, createRoot } from "react-dom/client";

import { has } from "../utils/helpers/objects";
import setImmediate from "setimmediate";

import Cache from "../utils/browser/Cache";

import xhr from "../utils/fetch/xhr";

import { createBrowserHistory } from "history";
import configureStore from "../redux/store/configureStore";

import rehydrate from "./rehydrate";
import {
  getBasePathModularUI,
  getBasePathServer,
  getSetting,
} from "../constants/Settings";

import {
  setAllContentInDataSetting,
  setDateTimeSettings,
  setLoginPreferences,
} from "../redux/actions/Preferences";
import { showXHRErrorNotification } from "../redux/actions/Notification";
import { handleError } from "../redux/actions/Error";
import { loginSuccess } from "../redux/actions/SignIn";
import { updateEntryDate } from "../redux/actions/ModelCatalog";

import { locationChange, replace } from "../redux/_router/RouterActions";

import { JsonParseException, FetchException } from "../exceptions";

import { Init } from "./Init";

import { handleBeforeRenderHooks } from "../redux/store/beforeRenderHooks";

import type {
  ComponentType,
  Element as ReactElement,
  ElementType,
} from "react";
import type { Theme } from "../react-theme/types";
import type { CustomReducers, ReduxState, ReduxStore } from "../redux/types";
import type { RouterHistory } from "react-router";
import type { BeforeRenderHook } from "../redux/store/beforeRenderHooks";
import type { Props as FallbackProps } from "../react/ErrorBoundaryFallback";
import createTheme from "../react-theme/createTheme";
import { receiveLocale } from "../redux";

type RenderFunction = () => ReactElement<ElementType>;
type MountFunction = (
  applicationNode: Element,
  element: ReactElement<ElementType>,
) => void;
type Props = {
  customReducers?: CustomReducers,
  theme?: Theme | Array<Theme>,
  render: RenderFunction,
  mount: MountFunction,
  beforeRenderHooks?: Array<BeforeRenderHook>,
  ErrorFallbackComponent?: ComponentType<FallbackProps>,
};

/*
 * deserialize serialized data from the server to provide a smooth dehydration.
 */
const parseDataToJSON = (data: string) => {
  try {
    return JSON.parse(data);
  } catch (error) {
    throw new JsonParseException(`Error parsing content ${data}`);
  }
};

const getDataFromServer = () => {
  const dataElement = document.querySelector(
    'script[type="application/json"][data-app-state="app-json"]',
  );

  if (!dataElement) {
    throw new Error("Error loading state, json not found");
  } else if (dataElement.textContent.trim() === "") {
    return {};
  }

  return parseDataToJSON(dataElement.textContent);
};

/**
 */
export const setUnhandledRejectionEvent = (store: ReduxStore) => {
  // $FlowIssue
  globalThis.onunhandledrejection = (event) => {
    if (event.detail) {
      return setImmediate(() => {
        const errorMessage = event.detail.reason.message.toString();

        store.dispatch(showXHRErrorNotification(errorMessage));
        throw event.detail.reason;
      });
    }

    return event;
  };
};

/**
 */
const initThemeSettings = (state: ReduxState, theme?: Theme | Array<Theme>) => {
  const storedTheme = state.preferences.theme;
  // $FlowFixMe
  createTheme(storedTheme, theme); // on theme creation, the settings are set
};

const handleLocale = (store: ReduxStore) => {
  const locale = store.getState().i18n.locale;
  if (locale) {
    store.dispatch(receiveLocale(locale));
  }
};

/**
 * The redirectURI querystring parameter is available when the server is redirecting an unauthorized deep link
 * @param store
 */
const handleRedirectURI = (store: ReduxStore) => {
  // $FlowIssue
  const urlParams = new URLSearchParams(globalThis.location.search);
  const redirectURI = urlParams.get("redirectURI");
  if (redirectURI) {
    xhr({ url: `${getBasePathModularUI()}${redirectURI}` }).catch((e) => {
      if (
        e.id === "UnauthorizedException" ||
        e.id === "Error.NotAuthorized" ||
        e.id === "Error.Authentication.Required" ||
        e.id === "Error.Authentication.InvalidCredentials"
      ) {
        const LOGIN_PATH = getSetting("LOGIN_PATH", "/signin");
        store.dispatch(
          replace(LOGIN_PATH, {
            from: { pathname: redirectURI },
            modal: false,
          }),
        );
      }
    });
  }
};

/**
 */
export const setModelCatalogEntryDate = (store: ReduxStore) => {
  // $FlowIssue
  if (globalThis) {
    // $FlowIssue
    const { search } = globalThis.location;
    const entryDateFromUrl = new URLSearchParams(search).get("entryDate");
    if (entryDateFromUrl) {
      store.dispatch(updateEntryDate(entryDateFromUrl));
    } else if (Cache.hasItem("ModelCatalogEntryDate")) {
      store.dispatch(Cache.getItem("ModelCatalogEntryDate"));
    }
  }
};

/**
 */
export const setupClient = (
  customReducers?: CustomReducers,
  beforeRenderHooks: ?Array<BeforeRenderHook>,
  theme?: Theme | Array<Theme>,
): { store: ReduxStore, routerHistory: RouterHistory } => {
  // $FlowIssue
  if (globalThis.contextPath === undefined) {
    throw new Error("Missing contextPath on window object");
  }

  const data = getDataFromServer();

  // remove all resources from cache
  Cache.clear("^res:");

  // $FlowExpectedError
  const browserHistory: RouterHistory = createBrowserHistory({
    basename: getBasePathServer(),
  });
  const { routerHistory, store } = configureStore(
    browserHistory,
    customReducers ?? {},
    rehydrate(data),
  );

  initThemeSettings(store.getState(), theme);

  handleLocale(store);

  handleRedirectURI(store);
  setModelCatalogEntryDate(store);

  setAllContentInDataSetting(store.getState());
  setLoginPreferences(store.getState());
  setDateTimeSettings(store.getState());

  // load existing cache from other browser tabs
  Cache.loadOtherBrowserTabs(() => {
    if (Cache.getItem("auth")) {
      store.dispatch(loginSuccess());
    }
  });

  if (has(data, "error.name")) {
    const error = new FetchException(data?.error?.response);
    store.dispatch(handleError(error));
  }

  if (Cache.getItem("auth")) {
    store.dispatch(loginSuccess());
  }

  // listen to history change and update the redux router store
  routerHistory.listen((location, action) => {
    store.dispatch(locationChange(location, action));
  });

  setUnhandledRejectionEvent(store);

  if (document.body) {
    document.body.className = "js";
  }

  if (beforeRenderHooks) {
    handleBeforeRenderHooks(beforeRenderHooks, { store });
  }

  return { store, routerHistory };
};

/**
 */
export const addContentLoadedEvent = (
  store: ReduxStore,
  routerHistory: RouterHistory,
  theme?: Theme | Array<Theme>,
  render: RenderFunction,
  ErrorFallbackComponent?: ComponentType<FallbackProps>,
  mount: MountFunction,
) => {
  // $FlowIssue
  globalThis.addEventListener("DOMContentLoaded", () => {
    const applicationNode = document.querySelector("#application");
    if (!applicationNode) {
      throw new Error(
        "No DOM element with id application found to attach client to",
      );
    }

    mount(
      applicationNode,
      <Init
        store={store}
        routerHistory={routerHistory}
        // $FlowIssue
        contextPath={globalThis.contextPath}
        theme={theme}
        ErrorFallbackComponent={ErrorFallbackComponent}
      >
        {render()}
      </Init>,
    );
  });
};

/**
 */
const mountClient = (
  applicationNode: Element,
  element: ReactElement<ElementType>,
) => {
  const isSSR = applicationNode.querySelector(".application");
  if (isSSR) {
    hydrateRoot(element, applicationNode);
  } else {
    const root = createRoot(element);
    root.render(applicationNode);
  }
};

/**
 * Mount the webapplication to the DOM, setup redux store and caches, add unhandledRejectionEvent, used client side when JavaScript is enabled.
 */
export const client = ({
  customReducers,
  theme,
  render,
  beforeRenderHooks,
  ErrorFallbackComponent,
  mount,
}: Props) => {
  const { store, routerHistory } = setupClient(
    customReducers,
    beforeRenderHooks,
  );

  addContentLoadedEvent(
    store,
    routerHistory,
    theme,
    render,
    ErrorFallbackComponent,
    mount ?? mountClient,
  );
};
